import { X } from 'lucide-react';
import { Dialog as D } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './button';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizes = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

/**
 * The one modal. Focus trap, Escape and click-outside come from Radix. It is centred and never
 * taller or wider than the viewport (dvh, so mobile browser bars don't hide the footer): the
 * header and footer stay put and the body scrolls.
 */
export function Dialog({ open, onOpenChange, title, description, children, footer, size = 'md' }: DialogProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-[1px] data-[state=open]:animate-fade-in" />
        <D.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border bg-surface shadow-pop data-[state=open]:animate-fade-in',
            sizes[size],
          )}
        >
          <PanelHeader title={title} description={description} />
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
          {footer ? <PanelFooter className="rounded-b-xl">{footer}</PanelFooter> : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/** Side panel for quick create/edit without leaving the list (HaloPSA style). Full width on a phone. */
export function Sheet({ open, onOpenChange, title, description, children, footer, width = 'max-w-xl' }: Omit<DialogProps, 'size'> & { width?: string }) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-slate-950/30 data-[state=open]:animate-fade-in" />
        <D.Content className={cn('fixed inset-y-0 right-0 z-50 flex h-dvh w-full flex-col border-l bg-surface shadow-pop data-[state=open]:animate-slide-in-right', width)}>
          <PanelHeader title={title} description={description} />
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
          {footer ? <PanelFooter>{footer}</PanelFooter> : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

function PanelHeader({ title, description }: { title: ReactNode; description?: ReactNode }) {
  return (
    <div className="flex shrink-0 items-start justify-between gap-4 border-b px-5 py-3.5">
      <div className="min-w-0">
        <D.Title className="text-base font-semibold break-words">{title}</D.Title>
        {description ? <D.Description className="mt-0.5 text-sm text-muted">{description}</D.Description> : <D.Description className="sr-only">{title}</D.Description>}
      </div>
      <D.Close asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Close" className="-mr-1.5 shrink-0">
          <X />
        </Button>
      </D.Close>
    </div>
  );
}

const PanelFooter = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('flex shrink-0 flex-wrap items-center justify-end gap-2 border-t bg-surface-2 px-5 py-3', className)}>{children}</div>
);
